import { Component, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { ZiroFieldAbstractDirective } from '../../../forms/ziro-field-abstract.directive';
import { ZiroFieldComponentConfig } from '../../../forms/ziro-forms-models';
import {
  DialPlanGroupEntry,
  DialPlanGroupEntryStatus,
  DialPlanGroupEntryType,
  Microsoft365UserLicenses,
} from '../../../shared/models/generated/ziroModels';
import { TranslateService } from '@ngx-translate/core';
import { DateTimeService } from '../../../shared/services/date-time.service';
import { ButtonStyles } from '../../../button/button.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { Microsoft365LicensesContext } from '../../../shared/contexts/microsoft-365-licenses.context';
import { first, Observable, Subscription, switchMap, tap } from 'rxjs';
import { UsageLocationContext } from '../../../shared/contexts/usage-location.context';

export class ZiroExtensionSelectorConfig extends ZiroFieldComponentConfig {
  constructor(
    public config: {
      options: DialPlanGroupEntry[];
      size: ExtensionSelectorSize;
      showRecentlyDeleted?: boolean;
      showUnavailable?: boolean;
      showInactive?: boolean;
    },
  ) {
    super();
  }
}

export enum ExtensionSelectorSize {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

export interface ExtensionSelectorOption extends DialPlanGroupEntry {
  tooltip: ExtensionSelectorOptionTooltip;
  isDisabled?: boolean;
}

export interface ExtensionSelectorOptionTooltip {
  message: string;
  params?: ExtensionSelectorOptionTooltipParams;
}

export interface UsageLocationModalProperties {
  valid: boolean;
  upn: string;
  userLocation: string;
  numberLocation: string;
}

interface ExtensionSelectorOptionTooltipParams {
  environmentType?: string;
  extensionDescription?: string;
  deletionDate?: string;
  exceptionGroupName?: string;
  usageType?: string;
  accountType?: string;
}

@Component({
  selector: 'ziro-extension-selector',
  templateUrl: './extension-selector.component.html',
  styleUrls: ['./extension-selector.component.scss'],
  providers: [{ provide: ZiroFieldAbstractDirective, useExisting: ExtensionSelectorComponent }],
  standalone: false,
})
export class ExtensionSelectorComponent
  extends ZiroFieldAbstractDirective<string, string, ZiroExtensionSelectorConfig>
  implements OnInit, OnDestroy
{
  private translateService = inject(TranslateService);
  private dateTimeService = inject(DateTimeService);
  private ziroModalService = inject(ZiroModalService);
  private _ms365LicensesContext = inject(Microsoft365LicensesContext);
  private _usageLocationContext = inject(UsageLocationContext, { optional: true });
  private _subscriptions = new Subscription();
  buttonStyles = ButtonStyles;

  readonly isSubmitting = input<boolean>();
  readonly isHosted = input(false);
  usageLocationProps = input<UsageLocationModalProperties>(null);
  msLicenses = input<Microsoft365UserLicenses>();
  tenantId = input<string>();
  usageLocationUpdated = output<string>();

  PAGE_SIZE = 24;
  ENVIRONMENT_TYPE_TOOLTIP_MAP: Map<DialPlanGroupEntryType, string> = new Map([
    [
      DialPlanGroupEntryType.CUCM,
      this.translateService.instant('tkey;extension_selector.option.tooltip.environment_type.cucm'),
    ],
    [
      DialPlanGroupEntryType.TEAMS,
      this.translateService.instant('tkey;extension_selector.option.tooltip.environment_type.teams'),
    ],
    [
      DialPlanGroupEntryType.WEBEX,
      this.translateService.instant('tkey;extension_selector.option.tooltip.environment_type.webex'),
    ],
  ]);

  DialPlanGroupEntryStatus = DialPlanGroupEntryStatus;
  ExtensionSelectorSize = ExtensionSelectorSize;

  // from component config
  configOptions: DialPlanGroupEntry[];
  options: ExtensionSelectorOption[] = [];
  size: ExtensionSelectorSize;
  showRecentlyDeleted?: boolean;
  showUnavailable?: boolean;
  showInactive?: boolean;

  initialValue: string;
  filteredOptions: ExtensionSelectorOption[] = [];
  displayedFilteredOptions: ExtensionSelectorOption[] = [];
  isNoNumbersRemaining = false;
  page = 1;
  filter = '';

  protected requiredFeedbackMessage = 'tkey;dndidselection.dndid.error.select.extension.text';
  protected validationInProgressMessage = '';

  ngOnInit(): void {
    this.requiredFeedbackMessage = this.isHosted()
      ? 'tkey;dndidselection.dndid.error.select.number.text'
      : 'tkey;dndidselection.dndid.error.select.extension.text';
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  applyComponentConfig: ({ config }: ZiroExtensionSelectorConfig) => void = ({
    config,
  }: ZiroExtensionSelectorConfig) => {
    this.size = config.size;
    this.showRecentlyDeleted = !!config.showRecentlyDeleted;
    this.showUnavailable = !!config.showUnavailable;
    this.showInactive = !!config.showInactive;

    this.initialValue = this.initialValue ?? this.fieldData;

    let isFiltered = false;
    if (config.options !== this.configOptions) {
      this.configOptions = config.options;
      this._setOptions(config.options);
      this.page = 1;

      if (this.initialValue) {
        const initialDialPlanEntry = this.options.find((option) => option.value === this.initialValue);
        if (initialDialPlanEntry) {
          initialDialPlanEntry.assignmentStatus = DialPlanGroupEntryStatus.UNASSIGNED;
          this._setFilteredOptions(this.filter);
          isFiltered = true;
          const optionNumber = this.filteredOptions.indexOf(initialDialPlanEntry) + 1;
          this.page = Math.ceil(optionNumber / this.PAGE_SIZE);
        }
      }
    }

    if (!isFiltered) {
      this._setFilteredOptions(this.filter);
    }

    this._setPaginatedFilteredOptions();
  };

  onFilterChanged($event: string): void {
    this.filter = $event;
    this._setFilteredOptions(this.filter);
    this.page = 1;
    this._setPaginatedFilteredOptions();
  }

  onOptionSelected($event: string): void {
    if (this.fieldData === $event) {
      this.updateSelf(null);
    } else {
      this.updateSelf($event);
    }
  }

  onPageChange(): void {
    this._setPaginatedFilteredOptions();
  }

  openUsageLocationModal(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: this.ziroIcons.LOCATION,
        title: this.translateService.instant('tkey;microsoft_360.view.teams.usage_location.update_modal.title'),
        promptBody: this.translateService.instant('tkey;microsoft_360.view.teams.usage_location.update_modal.message', {
          upn: this.usageLocationProps().upn,
          userLocation: this.usageLocationProps().userLocation,
          numberLocation: this.usageLocationProps().numberLocation,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'prompt-modal-cancel-button',
          },
          {
            label: 'tkey;microsoft_360.view.teams.usage_location.update_modal.button.update',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'prompt-modal-confirm-button',
            cb: () =>
              this._ms365LicensesContext
                .updateUserLicenses(this.tenantId(), this.usageLocationProps().upn, {
                  ...this.msLicenses(),
                  usageLocation: this.usageLocationProps().numberLocation,
                })
                .pipe(
                  switchMap(() => this._poll360View()),
                  first((isPolling) => !isPolling),
                  tap(() => this.usageLocationUpdated.emit(this.usageLocationProps().numberLocation)),
                ),
          },
        ],
      },
    };

    this.ziroModalService.openPromptModal(() => options.modalViewProperties, options).subscribe();
  }

  private _poll360View(): Observable<boolean> {
    this._usageLocationContext.startPolling(this.usageLocationProps().upn);
    return this._usageLocationContext.state$;
  }

  private _setOptions(dialPlanEntries: DialPlanGroupEntry[] | ExtensionSelectorOption[]): void {
    this.options = dialPlanEntries.map((entry): ExtensionSelectorOption => {
      if ('tooltip' in entry) {
        return entry;
      }
      let tooltip: ExtensionSelectorOptionTooltip = null;
      switch (entry.assignmentStatus) {
        case DialPlanGroupEntryStatus.ASSIGNED:
          tooltip = {
            message: 'tkey;extension_selector.option.tooltip.assigned',
            params: {
              environmentType: this.ENVIRONMENT_TYPE_TOOLTIP_MAP.get(entry.pbxType),
              extensionDescription: entry.description,
            },
          };
          break;
        case DialPlanGroupEntryStatus.RESTRICTED:
          tooltip = {
            message: 'tkey;extension_selector.option.tooltip.restricted',
            params: {
              environmentType: this.ENVIRONMENT_TYPE_TOOLTIP_MAP.get(entry.pbxType),
              exceptionGroupName: entry.exceptionGroupName,
            },
          };
          break;
        case DialPlanGroupEntryStatus.RECENTLY_DELETED:
          tooltip = {
            message: 'tkey;extension_selector.option.tooltip.recently_deleted',
            params: {
              environmentType: this.ENVIRONMENT_TYPE_TOOLTIP_MAP.get(entry.pbxType),
              extensionDescription: entry.description,
              deletionDate: this.dateTimeService.parse(entry.deletionDate).format('MMM DD YYYY, hh:mm:ss a'),
            },
          };
          break;
        case DialPlanGroupEntryStatus.INACTIVE:
          tooltip = {
            message: entry.description,
            params: {
              environmentType: this.ENVIRONMENT_TYPE_TOOLTIP_MAP.get(entry.pbxType),
            },
          };
          break;
        case DialPlanGroupEntryStatus.TEAMS_UNASSIGNED_NUMBER_ROUTING_RULE:
          tooltip = {
            message: 'tkey;extension_selector.option.tooltip.teams_unassigned_number_routing_rule',
            params: {
              extensionDescription: entry.description,
            },
          };
          break;
      }
      return { ...entry, tooltip } as ExtensionSelectorOption;
    });

    this.isNoNumbersRemaining = this.options.every(
      (option) =>
        option.assignmentStatus === DialPlanGroupEntryStatus.ASSIGNED ||
        option.assignmentStatus === DialPlanGroupEntryStatus.RESTRICTED,
    );
  }

  private _setFilteredOptions(filter: string): void {
    const filterValue = filter?.trim()?.toLowerCase();
    const filterValueStripped = filterValue?.replace(/[^0-9A-Za-z]/g, '');
    this.filteredOptions = this.options
      .filter((dialPlanEntry) => {
        if (
          !!dialPlanEntry?.tooltip?.message &&
          dialPlanEntry.tooltip.message ===
            'tkey;microsoft_360.view.teams.extensionSelection.tooltip.usage_account_type_mismatch'
        ) {
          return this.showUnavailable;
        }

        switch (dialPlanEntry.assignmentStatus) {
          case DialPlanGroupEntryStatus.RECENTLY_DELETED:
            return this.showRecentlyDeleted;
          case DialPlanGroupEntryStatus.INACTIVE:
            return this.showInactive;
          case DialPlanGroupEntryStatus.ASSIGNED:
          case DialPlanGroupEntryStatus.RESTRICTED:
          case DialPlanGroupEntryStatus.TEAMS_UNASSIGNED_NUMBER_ROUTING_RULE:
            return this.showUnavailable;
          case DialPlanGroupEntryStatus.UNASSIGNED:
          default:
            return true;
        }
      })
      .filter((dialPlanEntry) => {
        return (
          !filterValue ||
          dialPlanEntry.value.includes(filterValue) ||
          dialPlanEntry.value.includes(filterValueStripped) ||
          dialPlanEntry.description?.toLowerCase().includes(filterValue)
        );
      });
  }

  private _setPaginatedFilteredOptions(): void {
    this.displayedFilteredOptions = this.filteredOptions
      .sort((a, b) => this._stripFilteredValue(a.value) - this._stripFilteredValue(b.value))
      .slice((this.page - 1) * this.PAGE_SIZE, this.page * this.PAGE_SIZE);
  }

  private _stripFilteredValue(value: string): number {
    return parseInt(
      value
        .trim()
        ?.toLowerCase()
        .replace(/[^0-9A-Za-z]/g, ''),
    );
  }
}
